import React, { useState, useMemo } from 'react';
import './TalentPlan.css';

/* ─── Static Configuration ──────────────────────────────────────────────────── */

const ORG_LEVELS = [
  { id: 1, label: 'Entry Level',            description: 'Foundational roles, graduates, junior staff' },
  { id: 2, label: 'Operational Excellence', description: 'Mid-level professionals and team leads' },
  { id: 3, label: 'Senior Management',      description: 'Senior managers and department heads' },
  { id: 4, label: 'Executive',              description: 'C-suite and executive leadership' },
];

const DEVELOPMENT_OPTIONS = [
  { id: 'short_course',       label: 'Short Course',          icon: 'bi-mortarboard',      description: 'Targeted skill-building workshops and courses' },
  { id: 'coaching_mentoring', label: 'Coaching and Mentoring',  icon: 'bi-people',           description: 'One-on-one guidance from experienced professionals' },
  { id: 'academic',           label: 'Academic Training',     icon: 'bi-building-check',   description: 'Formal academic programmes and qualifications' },
  { id: 'on_the_job',         label: 'On-the-Job Training',   icon: 'bi-briefcase',        description: 'Structured work-based learning experiences' },
  { id: 'e_learning',         label: 'e-Learning',            icon: 'bi-laptop',           description: 'Self-paced digital learning modules and platforms' },
  { id: 'secondment',         label: 'Secondment / Rotation', icon: 'bi-arrow-left-right', description: 'Temporary role placement for exposure and growth' },
];

const COMPETENCY_AREAS = [
  'Strategic Outlook',
  'Leadership and Management',
  'Interpersonal and Communication',
  'Digital and Innovation',
  'Operational Excellence',
  'Technical and Professional',
];

const DEPARTMENTS = ['All Departments', 'Programs', 'Finance', 'Business Development', 'Digital Health', 'People and Culture', 'Executive'];
const STATUSES    = ['All Statuses', 'Planned', 'In Progress', 'Completed', 'Cancelled'];

const STATUS_STYLES = {
  'Planned':     { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
  'In Progress': { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
  'Completed':   { bg: '#f0f7f4', color: '#006B54', border: '#6ee7b7' },
  'Cancelled':   { bg: '#fef2f2', color: '#b91c1c', border: '#fecaca' },
};

const LEVEL_BG   = ['#eff6ff', '#f0fdf4', '#fefce8', '#fdf4ff'];
const LEVEL_TEXT = ['#1d4ed8', '#15803d', '#a16207', '#7e22ce'];

const MOCK_PLANS = [
  {
    id: 1, employee: 'Amara Osei', role: 'Programs Officer', department: 'Programs',
    level: 'Entry Level', levelId: 1,
    competencyArea: 'Strategic Outlook', weakness: 'Vision and Conceptualisation of Strategy',
    currentScore: 52, targetScore: 75,
    developmentType: 'Coaching and Mentoring', developmentTypeId: 'coaching_mentoring',
    learningCenter: 'Internal Leadership Academy', courseName: 'Strategic Thinking Fundamentals',
    duration: '3 months', startDate: '2026-02-01', endDate: '2026-04-30',
    coach: 'Dr. Kofi Mensah', status: 'In Progress',
    notes: 'Bi-weekly sessions focused on environmental scanning and strategic formulation.',
  },
  {
    id: 2, employee: 'Fatima Diallo', role: 'Finance Analyst', department: 'Finance',
    level: 'Operational Excellence', levelId: 2,
    competencyArea: 'Digital and Innovation', weakness: 'Data Analytics and Reporting',
    currentScore: 48, targetScore: 80,
    developmentType: 'Short Course', developmentTypeId: 'short_course',
    learningCenter: 'Coursera / University of Michigan', courseName: 'Applied Data Analytics with Excel and Power BI',
    duration: '6 weeks', startDate: '2026-03-10', endDate: '2026-04-21',
    coach: '', status: 'Planned',
    notes: 'Employee to complete modules during working hours (2 hrs/day allocated).',
  },
  {
    id: 3, employee: 'James Kariuki', role: 'Head of Business Development', department: 'Business Development',
    level: 'Senior Management', levelId: 3,
    competencyArea: 'Leadership and Management', weakness: 'Team Building and Collaboration',
    currentScore: 61, targetScore: 85,
    developmentType: 'Academic Training', developmentTypeId: 'academic',
    learningCenter: 'Strathmore Business School', courseName: 'Executive Leadership Programme (ELP)',
    duration: '6 months', startDate: '2026-01-15', endDate: '2026-07-15',
    coach: '', status: 'In Progress',
    notes: 'Weekend programme; attendance tracked monthly.',
  },
  {
    id: 4, employee: 'Nadia Boateng', role: 'Chief Executive Officer', department: 'Executive',
    level: 'Executive', levelId: 4,
    competencyArea: 'Strategic Outlook', weakness: 'Go-getter / Entrepreneurial Drive',
    currentScore: 70, targetScore: 90,
    developmentType: 'Coaching and Mentoring', developmentTypeId: 'coaching_mentoring',
    learningCenter: 'Executive Coaching Network', courseName: 'Executive Performance Coaching',
    duration: '12 months', startDate: '2026-01-01', endDate: '2026-12-31',
    coach: 'Prof. Angela Ofori', status: 'In Progress',
    notes: 'Monthly 360-degree review sessions included.',
  },
  {
    id: 5, employee: 'Kwame Asante', role: 'Digital Health Officer', department: 'Digital Health',
    level: 'Entry Level', levelId: 1,
    competencyArea: 'Digital and Innovation', weakness: 'Technology Adoption and Systems Thinking',
    currentScore: 55, targetScore: 78,
    developmentType: 'e-Learning', developmentTypeId: 'e_learning',
    learningCenter: 'LinkedIn Learning', courseName: 'Digital Transformation Fundamentals',
    duration: '4 weeks', startDate: '2026-04-01', endDate: '2026-04-30',
    coach: '', status: 'Planned',
    notes: 'Self-paced; progress report due at month end.',
  },
  {
    id: 6, employee: 'Ama Sarpong', role: 'HR Manager', department: 'People and Culture',
    level: 'Operational Excellence', levelId: 2,
    competencyArea: 'Interpersonal and Communication', weakness: 'Conflict Resolution and Mediation',
    currentScore: 58, targetScore: 82,
    developmentType: 'Short Course', developmentTypeId: 'short_course',
    learningCenter: 'CIPM Ghana', courseName: 'Workplace Conflict Resolution',
    duration: '2 days', startDate: '2026-03-20', endDate: '2026-03-21',
    coach: '', status: 'Completed',
    notes: 'Certificate obtained. Post-training evaluation scheduled.',
  },
  {
    id: 7, employee: 'Emmanuel Tetteh', role: 'Senior Programs Manager', department: 'Programs',
    level: 'Senior Management', levelId: 3,
    competencyArea: 'Operational Excellence', weakness: 'Project Management and Delivery',
    currentScore: 64, targetScore: 88,
    developmentType: 'Academic Training', developmentTypeId: 'academic',
    learningCenter: 'PMI / Project Management Institute', courseName: 'PMP Certification Preparation',
    duration: '5 months', startDate: '2026-02-15', endDate: '2026-07-15',
    coach: '', status: 'In Progress',
    notes: 'Study leave granted for exam preparation. Exam booked for July 2026.',
  },
  {
    id: 8, employee: 'Abena Mensah', role: 'Communications Officer', department: 'Programs',
    level: 'Entry Level', levelId: 1,
    competencyArea: 'Interpersonal and Communication', weakness: 'Report Writing and Documentation',
    currentScore: 60, targetScore: 80,
    developmentType: 'On-the-Job Training', developmentTypeId: 'on_the_job',
    learningCenter: 'Internal Mentorship Programme', courseName: 'Technical Writing and Reporting Skills',
    duration: '2 months', startDate: '2026-03-01', endDate: '2026-04-30',
    coach: 'James Kariuki', status: 'In Progress',
    notes: 'Weekly writing assignments reviewed by supervisor.',
  },
];

/* ─── Helper Components ──────────────────────────────────────────────────────── */

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || { bg: '#f3f4f6', color: '#6b7280', border: '#e5e7eb' };
  return (
    <span className="tdp-badge" style={{ background: s.bg, color: s.color, borderColor: s.border }}>
      {status}
    </span>
  );
}

function LevelBadge({ level, levelId }) {
  const idx = Math.min((levelId || 1) - 1, 3);
  return (
    <span className="tdp-badge" style={{ background: LEVEL_BG[idx], color: LEVEL_TEXT[idx], borderColor: LEVEL_BG[idx] }}>
      L{levelId} · {level}
    </span>
  );
}

function ScoreBar({ current, target }) {
  const color = current < 65 ? '#dc2626' : current < 75 ? '#f59e0b' : current < 90 ? '#3b82f6' : '#10b981';
  return (
    <div className="tdp-score-bar-wrap">
      <div className="tdp-score-values">
        <span style={{ color }}>Current: <strong>{current}%</strong></span>
        <span style={{ color: '#6b7280' }}>Target: <strong>{target}%</strong></span>
      </div>
      <div className="tdp-score-track">
        <div className="tdp-score-fill" style={{ width: `${Math.min(current, 100)}%`, background: color }} />
        <div className="tdp-score-target" style={{ left: `${Math.min(target, 100)}%` }} title={`Target: ${target}%`} />
      </div>
    </div>
  );
}

/* ─── Detail Drawer ──────────────────────────────────────────────────────────── */

function PlanDetail({ item, onClose }) {
  if (!item) return null;
  const devOpt = DEVELOPMENT_OPTIONS.find(d => d.id === item.developmentTypeId);
  return (
    <div className="tdp-overlay" onClick={onClose}>
      <div className="tdp-drawer" onClick={e => e.stopPropagation()}>
        <div className="tdp-drawer-header">
          <div>
            <h3 className="tdp-drawer-title">{item.employee}</h3>
            <p className="tdp-drawer-subtitle">{item.role} · {item.department}</p>
          </div>
          <button className="tdp-close-btn" onClick={onClose} aria-label="Close">
            <i className="bi bi-x-lg" />
          </button>
        </div>

        <div className="tdp-drawer-body">
          <div className="tdp-detail-grid">
            <div className="tdp-detail-block">
              <span className="tdp-detail-label">Organisation Level</span>
              <LevelBadge level={item.level} levelId={item.levelId} />
            </div>
            <div className="tdp-detail-block">
              <span className="tdp-detail-label">Plan Status</span>
              <StatusBadge status={item.status} />
            </div>
            <div className="tdp-detail-block">
              <span className="tdp-detail-label">Competency Area</span>
              <span className="tdp-detail-value">{item.competencyArea}</span>
            </div>
            <div className="tdp-detail-block">
              <span className="tdp-detail-label">Area of Weakness</span>
              <span className="tdp-detail-value">{item.weakness}</span>
            </div>
          </div>

          <div className="tdp-detail-section">
            <h4 className="tdp-section-title"><i className="bi bi-graph-up-arrow" /> Performance Gap</h4>
            <ScoreBar current={item.currentScore} target={item.targetScore} />
          </div>

          <div className="tdp-detail-section">
            <h4 className="tdp-section-title">
              <i className={`bi ${devOpt?.icon || 'bi-journal'}`} /> Development Intervention
            </h4>
            <div className="tdp-detail-grid">
              <div className="tdp-detail-block">
                <span className="tdp-detail-label">Development Type</span>
                <span className="tdp-detail-value">{item.developmentType}</span>
              </div>
              <div className="tdp-detail-block">
                <span className="tdp-detail-label">Learning Centre / Provider</span>
                <span className="tdp-detail-value">{item.learningCenter}</span>
              </div>
              <div className="tdp-detail-block tdp-detail-full">
                <span className="tdp-detail-label">Programme / Course Name</span>
                <span className="tdp-detail-value">{item.courseName}</span>
              </div>
              {item.coach && (
                <div className="tdp-detail-block">
                  <span className="tdp-detail-label">Assigned Coach / Mentor</span>
                  <span className="tdp-detail-value">{item.coach}</span>
                </div>
              )}
              <div className="tdp-detail-block">
                <span className="tdp-detail-label">Duration</span>
                <span className="tdp-detail-value">{item.duration}</span>
              </div>
              <div className="tdp-detail-block">
                <span className="tdp-detail-label">Start Date</span>
                <span className="tdp-detail-value">{item.startDate}</span>
              </div>
              <div className="tdp-detail-block">
                <span className="tdp-detail-label">End Date</span>
                <span className="tdp-detail-value">{item.endDate}</span>
              </div>
            </div>
          </div>

          {item.notes && (
            <div className="tdp-detail-section">
              <h4 className="tdp-section-title"><i className="bi bi-chat-text" /> Notes</h4>
              <p className="tdp-notes-text">{item.notes}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Create Plan Form ───────────────────────────────────────────────────────── */

const EMPTY_FORM = {
  employee: '', role: '', department: 'Programs', level: '1',
  competencyArea: '', weakness: '', currentScore: '', targetScore: '',
  developmentTypeId: '', learningCenter: '', courseName: '',
  duration: '', startDate: '', endDate: '', coach: '',
  status: 'Planned', notes: '',
};

const FORM_STEPS = ['Employee', 'Gap Assessment', 'Intervention Plan', 'Review and Confirm'];

function CreatePlanForm({ onClose, onSave }) {
  const [step, setStep]         = useState(1);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [feedback, setFeedback] = useState({ msg: '', type: '' });

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const validate = () => {
    const rules = [
      [1, (!form.employee || !form.role || !form.department), 'Please fill in employee name, role, and department.'],
      [2, (!form.competencyArea || !form.weakness || !form.currentScore || !form.targetScore), 'Please complete all gap assessment fields.'],
      [3, (!form.developmentTypeId || !form.learningCenter || !form.courseName), 'Please select a development type and provide the learning centre and course name.'],
    ];
    for (const [s, invalid, msg] of rules) {
      if (step === s && invalid) { setFeedback({ msg, type: 'error' }); return false; }
    }
    setFeedback({ msg: '', type: '' });
    return true;
  };

  const next   = () => { if (validate()) setStep(s => s + 1); };
  const back   = () => { setStep(s => s - 1); setFeedback({ msg: '', type: '' }); };

  const submit = () => {
    if (!validate()) return;
    const levelObj = ORG_LEVELS.find(l => l.id === parseInt(form.level));
    const devObj   = DEVELOPMENT_OPTIONS.find(d => d.id === form.developmentTypeId);
    onSave({
      ...form,
      id: Date.now(),
      levelId: parseInt(form.level),
      level: levelObj?.label || '',
      developmentType: devObj?.label || '',
      currentScore: parseInt(form.currentScore),
      targetScore:  parseInt(form.targetScore),
    });
    setFeedback({ msg: 'Corporate talent plan created successfully.', type: 'success' });
    setTimeout(onClose, 1100);
  };

  const reviewField = (label, val) => val ? (
    <div className="tdp-review-row">
      <span className="tdp-review-label">{label}</span>
      <span className="tdp-review-val">{val}</span>
    </div>
  ) : null;

  return (
    <div className="tdp-overlay" onClick={onClose}>
      <div className="tdp-modal" onClick={e => e.stopPropagation()}>
        <div className="tdp-drawer-header">
          <div>
            <h3 className="tdp-drawer-title">New Corporate Development Plan</h3>
            <p className="tdp-drawer-subtitle">Create an organisation-wide talent development record</p>
          </div>
          <button className="tdp-close-btn" onClick={onClose} aria-label="Close"><i className="bi bi-x-lg" /></button>
        </div>

        <div className="tdp-stepbar">
          {FORM_STEPS.map((s, i) => (
            <div key={s} className={`tdp-step ${step === i + 1 ? 'active' : step > i + 1 ? 'done' : ''}`}>
              <div className="tdp-step-circle">
                {step > i + 1 ? <i className="bi bi-check" /> : i + 1}
              </div>
              <span className="tdp-step-lbl">{s}</span>
              {i < FORM_STEPS.length - 1 && <div className="tdp-step-line" />}
            </div>
          ))}
        </div>

        <div className="tdp-modal-body">
          {feedback.msg && (
            <div className={`tdp-feedback tdp-fb-${feedback.type}`}>{feedback.msg}</div>
          )}

          {/* Step 1 — Employee */}
          {step === 1 && (
            <div className="tdp-form-grid">
              <div className="tdp-fg tdp-fg-full">
                <label>Employee Full Name *</label>
                <input value={form.employee} onChange={e => set('employee', e.target.value)} placeholder="e.g. Amara Osei" />
              </div>
              <div className="tdp-fg">
                <label>Job Role / Title *</label>
                <input value={form.role} onChange={e => set('role', e.target.value)} placeholder="e.g. Programs Officer" />
              </div>
              <div className="tdp-fg">
                <label>Department *</label>
                <select value={form.department} onChange={e => set('department', e.target.value)}>
                  {DEPARTMENTS.filter(d => d !== 'All Departments').map(d => <option key={d}>{d}</option>)}
                </select>
              </div>
              <div className="tdp-fg tdp-fg-full">
                <label>Organisation Level *</label>
                <div className="tdp-level-picker">
                  {ORG_LEVELS.map(l => (
                    <label key={l.id} className={`tdp-level-card-opt ${form.level === String(l.id) ? 'selected' : ''}`}>
                      <input type="radio" name="lvl" value={l.id} checked={form.level === String(l.id)} onChange={e => set('level', e.target.value)} />
                      <div className="tdp-lc-num" style={{ color: LEVEL_TEXT[l.id - 1] }}>Level {l.id}</div>
                      <div className="tdp-lc-name">{l.label}</div>
                      <div className="tdp-lc-desc">{l.description}</div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2 — Gap Assessment */}
          {step === 2 && (
            <div className="tdp-form-grid">
              <div className="tdp-fg">
                <label>Competency Area *</label>
                <select value={form.competencyArea} onChange={e => set('competencyArea', e.target.value)}>
                  <option value="">Select area...</option>
                  {COMPETENCY_AREAS.map(a => <option key={a}>{a}</option>)}
                </select>
              </div>
              <div className="tdp-fg">
                <label>Specific Area of Weakness *</label>
                <input value={form.weakness} onChange={e => set('weakness', e.target.value)} placeholder="e.g. Vision and Conceptualisation of Strategy" />
              </div>
              <div className="tdp-fg">
                <label>Current Performance Score (%) *</label>
                <input type="number" min={0} max={100} value={form.currentScore} onChange={e => set('currentScore', e.target.value)} placeholder="e.g. 52" />
              </div>
              <div className="tdp-fg">
                <label>Target Performance Score (%) *</label>
                <input type="number" min={0} max={100} value={form.targetScore} onChange={e => set('targetScore', e.target.value)} placeholder="e.g. 80" />
              </div>
              {form.currentScore && form.targetScore && (
                <div className="tdp-fg tdp-fg-full">
                  <label>Gap Preview</label>
                  <ScoreBar current={parseInt(form.currentScore) || 0} target={parseInt(form.targetScore) || 0} />
                </div>
              )}
            </div>
          )}

          {/* Step 3 — Intervention Plan */}
          {step === 3 && (
            <div className="tdp-form-grid">
              <div className="tdp-fg tdp-fg-full">
                <label>Development Type *</label>
                <div className="tdp-devtype-picker">
                  {DEVELOPMENT_OPTIONS.map(d => (
                    <label key={d.id} className={`tdp-dt-card ${form.developmentTypeId === d.id ? 'selected' : ''}`}>
                      <input type="radio" name="devt" value={d.id} checked={form.developmentTypeId === d.id} onChange={e => set('developmentTypeId', e.target.value)} />
                      <i className={`bi ${d.icon} tdp-dt-icon`} />
                      <div className="tdp-dt-name">{d.label}</div>
                      <div className="tdp-dt-desc">{d.description}</div>
                    </label>
                  ))}
                </div>
              </div>
              <div className="tdp-fg">
                <label>Learning Centre / Provider *</label>
                <input value={form.learningCenter} onChange={e => set('learningCenter', e.target.value)} placeholder="e.g. Strathmore Business School" />
              </div>
              <div className="tdp-fg">
                <label>Programme / Course Name *</label>
                <input value={form.courseName} onChange={e => set('courseName', e.target.value)} placeholder="e.g. Executive Leadership Programme" />
              </div>
              {form.developmentTypeId === 'coaching_mentoring' && (
                <div className="tdp-fg">
                  <label>Assigned Coach / Mentor</label>
                  <input value={form.coach} onChange={e => set('coach', e.target.value)} placeholder="e.g. Dr. Kofi Mensah" />
                </div>
              )}
              <div className="tdp-fg">
                <label>Duration</label>
                <input value={form.duration} onChange={e => set('duration', e.target.value)} placeholder="e.g. 3 months" />
              </div>
              <div className="tdp-fg">
                <label>Start Date</label>
                <input type="date" value={form.startDate} onChange={e => set('startDate', e.target.value)} />
              </div>
              <div className="tdp-fg">
                <label>End Date</label>
                <input type="date" value={form.endDate} onChange={e => set('endDate', e.target.value)} />
              </div>
              <div className="tdp-fg tdp-fg-full">
                <label>Notes / Instructions</label>
                <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={3} placeholder="Additional guidance or context for this plan..." />
              </div>
            </div>
          )}

          {/* Step 4 — Review */}
          {step === 4 && (
            <div className="tdp-review">
              <div className="tdp-review-section">
                <h4><i className="bi bi-person-badge" /> Employee Details</h4>
                {reviewField('Name', form.employee)}
                {reviewField('Role', form.role)}
                {reviewField('Department', form.department)}
                {reviewField('Organisation Level', `Level ${form.level} · ${ORG_LEVELS.find(l => l.id === parseInt(form.level))?.label}`)}
              </div>
              <div className="tdp-review-section">
                <h4><i className="bi bi-exclamation-triangle" /> Gap Assessment</h4>
                {reviewField('Competency Area', form.competencyArea)}
                {reviewField('Area of Weakness', form.weakness)}
                {reviewField('Current Score', `${form.currentScore}%`)}
                {reviewField('Target Score', `${form.targetScore}%`)}
              </div>
              <div className="tdp-review-section">
                <h4><i className="bi bi-journal-check" /> Intervention Plan</h4>
                {reviewField('Development Type', DEVELOPMENT_OPTIONS.find(d => d.id === form.developmentTypeId)?.label)}
                {reviewField('Learning Centre', form.learningCenter)}
                {reviewField('Course Name', form.courseName)}
                {reviewField('Coach / Mentor', form.coach)}
                {reviewField('Duration', form.duration)}
                {reviewField('Start Date', form.startDate)}
                {reviewField('End Date', form.endDate)}
              </div>
              {form.notes && (
                <div className="tdp-review-section">
                  <h4><i className="bi bi-chat-text" /> Notes</h4>
                  <p className="tdp-notes-text">{form.notes}</p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="tdp-modal-footer">
          {step > 1 && <button className="tdp-btn-outline" onClick={back}><i className="bi bi-arrow-left" /> Back</button>}
          <span style={{ flex: 1 }} />
          {step < 4 && (
            <button className="tdp-btn-primary" onClick={next}>
              Next <i className="bi bi-arrow-right" />
            </button>
          )}
          {step === 4 && (
            <button className="tdp-btn-primary" onClick={submit}>
              <i className="bi bi-check-lg" /> Create Plan
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Main Component ─────────────────────────────────────────────────────────── */

export default function CorporateTalentPlan() {
  const [plans,        setPlans]        = useState(MOCK_PLANS);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showCreate,   setShowCreate]   = useState(false);
  const [filterDept,   setFilterDept]   = useState('All Departments');
  const [filterLevel,  setFilterLevel]  = useState('');
  const [filterType,   setFilterType]   = useState('');
  const [filterStatus, setFilterStatus] = useState('All Statuses');
  const [search,       setSearch]       = useState('');

  const filtered = useMemo(() => plans.filter(i => {
    if (filterDept   !== 'All Departments' && i.department      !== filterDept)   return false;
    if (filterLevel  && String(i.levelId)  !== filterLevel)                       return false;
    if (filterType   && i.developmentTypeId !== filterType)                       return false;
    if (filterStatus !== 'All Statuses'    && i.status          !== filterStatus) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!i.employee.toLowerCase().includes(q) &&
          !i.competencyArea.toLowerCase().includes(q) &&
          !i.department.toLowerCase().includes(q)) return false;
    }
    return true;
  }), [plans, filterDept, filterLevel, filterType, filterStatus, search]);

  const stats = useMemo(() => ({
    total:      plans.length,
    planned:    plans.filter(i => i.status === 'Planned').length,
    inProgress: plans.filter(i => i.status === 'In Progress').length,
    completed:  plans.filter(i => i.status === 'Completed').length,
  }), [plans]);

  return (
    <div className="tdp-root">

      {/* ── Header ── */}
      <div className="tdp-header">
        <div className="tdp-header-left">
          <div className="tdp-breadcrumb">
            <i className="bi bi-grid-3x3-gap" /> Talent Development
            <i className="bi bi-chevron-right" /> Corporate
          </div>
          <h1 className="tdp-title">Corporate Talent Development Plan</h1>
          <p className="tdp-subtitle">
            Organisation-wide development plans mapped to competency gaps across all departments and levels
          </p>
        </div>
        <button className="tdp-btn-primary" onClick={() => setShowCreate(true)}>
          <i className="bi bi-plus-lg" /> New Plan
        </button>
      </div>

      {/* ── Stats ── */}
      <div className="tdp-stats-row">
        {[
          { label: 'Total Plans',  value: stats.total,      icon: 'bi-journal-richtext', color: '#006B54' },
          { label: 'Planned',      value: stats.planned,    icon: 'bi-clock',            color: '#1d4ed8' },
          { label: 'In Progress',  value: stats.inProgress, icon: 'bi-arrow-repeat',     color: '#15803d' },
          { label: 'Completed',    value: stats.completed,  icon: 'bi-check-circle',     color: '#059669' },
        ].map(s => (
          <div key={s.label} className="tdp-stat-card">
            <i className={`bi ${s.icon} tdp-stat-icon`} style={{ color: s.color }} />
            <div className="tdp-stat-val" style={{ color: s.color }}>{s.value}</div>
            <div className="tdp-stat-lbl">{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Level Overview ── */}
      <div className="tdp-level-overview">
        {ORG_LEVELS.map(l => {
          const count = plans.filter(i => i.levelId === l.id).length;
          return (
            <div key={l.id} className="tdp-level-card" style={{ borderTopColor: LEVEL_TEXT[l.id - 1] }}>
              <div className="tdp-lo-badge" style={{ background: LEVEL_BG[l.id - 1], color: LEVEL_TEXT[l.id - 1] }}>
                L{l.id}
              </div>
              <div className="tdp-lo-name">{l.label}</div>
              <div className="tdp-lo-count" style={{ color: LEVEL_TEXT[l.id - 1] }}>{count}</div>
              <div className="tdp-lo-sub">active plans</div>
            </div>
          );
        })}
      </div>

      {/* ── Filters ── */}
      <div className="tdp-filters">
        <div className="tdp-search-wrap">
          <i className="bi bi-search tdp-search-icon" />
          <input
            className="tdp-search"
            placeholder="Search employee, department, competency area..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <select className="tdp-filter-select" value={filterDept} onChange={e => setFilterDept(e.target.value)}>
          {DEPARTMENTS.map(d => <option key={d}>{d}</option>)}
        </select>
        <select className="tdp-filter-select" value={filterLevel} onChange={e => setFilterLevel(e.target.value)}>
          <option value="">All Levels</option>
          {ORG_LEVELS.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
        </select>
        <select className="tdp-filter-select" value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">All Dev. Types</option>
          {DEVELOPMENT_OPTIONS.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
        </select>
        <select className="tdp-filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          {STATUSES.map(s => <option key={s}>{s}</option>)}
        </select>
      </div>

      {/* ── Table ── */}
      <div className="tdp-table-wrap">
        <table className="tdp-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Department</th>
              <th>Level</th>
              <th>Competency Gap</th>
              <th>Development Type</th>
              <th>Duration</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="tdp-empty">
                  <i className="bi bi-inbox tdp-empty-icon" />
                  <div>No development plans match your filters.</div>
                </td>
              </tr>
            ) : filtered.map(item => {
              const devOpt = DEVELOPMENT_OPTIONS.find(d => d.id === item.developmentTypeId);
              return (
                <tr key={item.id}>
                  <td>
                    <div className="tdp-emp-name">{item.employee}</div>
                    <div className="tdp-emp-role">{item.role}</div>
                  </td>
                  <td className="tdp-muted">{item.department}</td>
                  <td><LevelBadge level={item.level} levelId={item.levelId} /></td>
                  <td>
                    <div className="tdp-gap-area">{item.competencyArea}</div>
                    <div className="tdp-gap-weak">{item.weakness}</div>
                  </td>
                  <td>
                    <div className="tdp-dt-cell">
                      <i className={`bi ${devOpt?.icon || 'bi-journal'}`} />
                      <span>{item.developmentType}</span>
                    </div>
                  </td>
                  <td className="tdp-muted">{item.duration}</td>
                  <td><StatusBadge status={item.status} /></td>
                  <td>
                    <button className="tdp-view-btn" onClick={() => setSelectedItem(item)}>
                      <i className="bi bi-eye" /> View
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selectedItem && (
        <PlanDetail item={selectedItem} onClose={() => setSelectedItem(null)} />
      )}
      {showCreate && (
        <CreatePlanForm
          onClose={() => setShowCreate(false)}
          onSave={item => setPlans(prev => [item, ...prev])}
        />
      )}
    </div>
  );
}
